class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onInlineMobileFacetToggle = this.onInlineMobileFacetToggle.bind(this);
    this.focusFlowyardMobileDrawer = this.focusFlowyardMobileDrawer.bind(this);
    this.closeFlowyardMobileDrawer = this.closeFlowyardMobileDrawer.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    if (this.closest('.facets-container--flowyard-sidebar')) {
      this.addEventListener('click', this.onInlineMobileFacetToggle, true);
    }

    const flowyardMobileDrawer = this.closest('menu-drawer.flowyard-mobile-facets__filter');
    flowyardMobileDrawer
      ?.querySelector(':scope > details > summary')
      ?.addEventListener('click', this.focusFlowyardMobileDrawer);
    if (flowyardMobileDrawer) {
      this.addEventListener('keyup', this.closeFlowyardMobileDrawer, true);
    }
  }

  focusFlowyardMobileDrawer(event) {
    const disclosure = event.currentTarget.parentElement;
    requestAnimationFrame(() => {
      if (!disclosure.open) return;
      disclosure.querySelector('.mobile-facets__details > summary')?.focus();
    });
  }

  closeFlowyardMobileDrawer(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const drawer = this.closest('menu-drawer.flowyard-mobile-facets__filter');
    const disclosure = drawer?.querySelector(':scope > details[open]');
    if (!disclosure) return;

    event.preventDefault();
    event.stopPropagation();
    drawer.closeMenuDrawer(event, disclosure.querySelector(':scope > summary'));
  }

  onInlineMobileFacetToggle(event) {
    const summary = event.target.closest('.mobile-facets__details > summary');
    if (!summary || !this.contains(summary)) return;

    event.stopPropagation();
    requestAnimationFrame(() => {
      summary.setAttribute('aria-expanded', summary.parentElement.open ? 'true' : 'false');
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
      '#FacetSortMobileForm',
      '.flowyard-mobile-facets__results',
    ];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const sourceForm = event.target.closest('form');
    const mobileFormIds = ['FacetFiltersFormMobile', 'FacetSortMobileForm'];
    const desktopFormIds = ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'];
    const isMobile = sourceForm ? mobileFormIds.includes(sourceForm.id) : false;
    const forms = [];

    sortFilterForms.forEach((form) => {
      if (isMobile && mobileFormIds.includes(form.id)) {
        forms.push(this.createSearchParams(form));
      } else if (!isMobile && desktopFormIds.includes(form.id)) {
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.filter(Boolean).join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  static minimumPriceCache = new Map();

  connectedCallback() {
    if (this.initialized) return;

    this.minimumParam = this.querySelector('[data-price-param="min"]');
    this.maximumParam = this.querySelector('[data-price-param="max"]');
    this.minimumSlider = this.querySelector('[data-price-slider="min"]');
    this.maximumSlider = this.querySelector('[data-price-slider="max"]');
    this.minimumInput = this.querySelector('[data-price-input="min"]');
    this.maximumInput = this.querySelector('[data-price-input="max"]');

    if (
      !this.minimumParam ||
      !this.maximumParam ||
      !this.minimumSlider ||
      !this.maximumSlider ||
      !this.minimumInput ||
      !this.maximumInput
    ) {
      return;
    }

    this.initialized = true;
    this.rangeMinimum = this.toNumber(this.dataset.rangeMin, 0);
    this.rangeMaximum = this.toNumber(this.dataset.rangeMax, this.rangeMinimum);
    this.minimumParam.disabled = false;
    this.maximumParam.disabled = false;

    this.minimumSlider.addEventListener('input', this.onSliderInput.bind(this));
    this.maximumSlider.addEventListener('input', this.onSliderInput.bind(this));
    this.minimumInput.addEventListener('input', this.onNumberInput.bind(this));
    this.maximumInput.addEventListener('input', this.onNumberInput.bind(this));
    this.minimumInput.addEventListener('change', this.onNumberChange.bind(this));
    this.maximumInput.addEventListener('change', this.onNumberChange.bind(this));

    this.setBounds(this.rangeMinimum, this.rangeMaximum);
    this.loadExactMinimum();
  }

  toNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  normalize(value) {
    return Math.round(value * 100) / 100;
  }

  serialize(value) {
    return String(this.normalize(value));
  }

  clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  setBounds(minimum, maximum) {
    this.rangeMinimum = this.normalize(Math.max(0, minimum));
    this.rangeMaximum = this.normalize(Math.max(this.rangeMinimum, maximum));

    const selectedMinimum = this.minimumParam.value
      ? this.toNumber(this.minimumParam.value, this.rangeMinimum)
      : this.rangeMinimum;
    const selectedMaximum = this.maximumParam.value
      ? this.toNumber(this.maximumParam.value, this.rangeMaximum)
      : this.rangeMaximum;

    [this.minimumSlider, this.maximumSlider, this.minimumInput, this.maximumInput].forEach((input) => {
      input.min = this.serialize(this.rangeMinimum);
      input.max = this.serialize(this.rangeMaximum);
    });

    const disabled = this.rangeMaximum <= this.rangeMinimum;
    this.minimumSlider.disabled = disabled;
    this.maximumSlider.disabled = disabled;
    this.minimumInput.disabled = disabled;
    this.maximumInput.disabled = disabled;
    this.toggleAttribute('data-single-value', disabled);

    this.setValues(selectedMinimum, selectedMaximum);
  }

  setValues(minimum, maximum) {
    const nextMinimum = this.clamp(this.normalize(minimum), this.rangeMinimum, this.rangeMaximum);
    const nextMaximum = this.clamp(this.normalize(maximum), nextMinimum, this.rangeMaximum);

    this.minimumSlider.value = this.serialize(nextMinimum);
    this.maximumSlider.value = this.serialize(nextMaximum);
    this.minimumInput.value = this.serialize(nextMinimum);
    this.maximumInput.value = this.serialize(nextMaximum);
    this.minimumInput.max = this.serialize(nextMaximum);
    this.maximumInput.min = this.serialize(nextMinimum);

    this.minimumParam.value = nextMinimum > this.rangeMinimum ? this.serialize(nextMinimum) : '';
    this.maximumParam.value = nextMaximum < this.rangeMaximum ? this.serialize(nextMaximum) : '';

    this.updateTrack(nextMinimum, nextMaximum);
    this.updateAccessibleValues(nextMinimum, nextMaximum);
  }

  onSliderInput(event) {
    const isMinimum = event.currentTarget === this.minimumSlider;
    const currentMinimum = this.toNumber(this.minimumSlider.value, this.rangeMinimum);
    const currentMaximum = this.toNumber(this.maximumSlider.value, this.rangeMaximum);

    if (isMinimum) {
      this.setValues(Math.min(currentMinimum, currentMaximum), currentMaximum);
    } else {
      this.setValues(currentMinimum, Math.max(currentMaximum, currentMinimum));
    }
  }

  onNumberInput(event) {
    if (event.priceRangeCommit) return;

    event.stopPropagation();

    const isMinimum = event.currentTarget === this.minimumInput;
    const rawValue = event.currentTarget.value;
    const fallback = isMinimum ? this.rangeMinimum : this.rangeMaximum;
    const value = rawValue === '' ? fallback : this.toNumber(rawValue, fallback);
    const currentMinimum = this.toNumber(this.minimumInput.value, this.rangeMinimum);
    const currentMaximum = this.toNumber(this.maximumInput.value, this.rangeMaximum);

    if (isMinimum) {
      this.minimumSlider.value = this.serialize(this.clamp(value, this.rangeMinimum, currentMaximum));
    } else {
      this.maximumSlider.value = this.serialize(this.clamp(value, currentMinimum, this.rangeMaximum));
    }

    this.updateTrack(
      this.toNumber(this.minimumSlider.value, this.rangeMinimum),
      this.toNumber(this.maximumSlider.value, this.rangeMaximum),
    );
  }

  onNumberChange(event) {
    const isMinimum = event.currentTarget === this.minimumInput;
    const currentMinimum = this.minimumInput.value === ''
      ? this.rangeMinimum
      : this.toNumber(this.minimumInput.value, this.rangeMinimum);
    const currentMaximum = this.maximumInput.value === ''
      ? this.rangeMaximum
      : this.toNumber(this.maximumInput.value, this.rangeMaximum);

    if (isMinimum) {
      this.setValues(Math.min(currentMinimum, currentMaximum), currentMaximum);
    } else {
      this.setValues(currentMinimum, Math.max(currentMaximum, currentMinimum));
    }

    const commitEvent = new Event('input', { bubbles: true });
    commitEvent.priceRangeCommit = true;
    event.currentTarget.dispatchEvent(commitEvent);
  }

  updateTrack(minimum, maximum) {
    const span = this.rangeMaximum - this.rangeMinimum;
    const minimumPercent = span > 0 ? ((minimum - this.rangeMinimum) / span) * 100 : 0;
    const maximumPercent = span > 0 ? ((maximum - this.rangeMinimum) / span) * 100 : 100;

    this.style.setProperty('--price-range-start', `${minimumPercent}%`);
    this.style.setProperty('--price-range-end', `${maximumPercent}%`);
    this.minimumSlider.classList.toggle('price-range__range--front', maximumPercent - minimumPercent < 5);
  }

  updateAccessibleValues(minimum, maximum) {
    this.minimumSlider.setAttribute('aria-valuetext', this.formatMoney(minimum));
    this.maximumSlider.setAttribute('aria-valuetext', this.formatMoney(maximum));
  }

  formatMoney(value) {
    const currency = window.Shopify?.currency?.active;
    if (!currency) return this.serialize(value);

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(value);
    } catch (_error) {
      return this.serialize(value);
    }
  }

  getMinimumPriceUrl() {
    const sectionId = document.getElementById('product-grid')?.dataset.id;
    if (!sectionId) return null;

    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete(this.minimumParam.name);
    url.searchParams.delete(this.maximumParam.name);
    url.searchParams.set('sort_by', 'price-ascending');
    url.searchParams.set('section_id', sectionId);
    return url;
  }

  async loadExactMinimum() {
    const url = this.getMinimumPriceUrl();
    if (!url) return;

    const cacheKey = url.toString();
    let request = PriceRange.minimumPriceCache.get(cacheKey);

    if (!request) {
      request = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to load price range minimum: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
          const firstProduct = parsedHtml.querySelector('[data-product-price-min]');
          const minimum = Number(firstProduct?.dataset.productPriceMin);
          if (!Number.isFinite(minimum)) throw new Error('Price range minimum is missing.');
          return minimum;
        });
      PriceRange.minimumPriceCache.set(cacheKey, request);
    }

    try {
      const minimum = await request;
      if (this.isConnected) this.setBounds(minimum, this.rangeMaximum);
    } catch (_error) {
      PriceRange.minimumPriceCache.delete(cacheKey);
    }
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
